import { API_CALL } from 'actions/actionConstants'
import { REOPEN_LOYALTY_ACCOUNT } from 'actions/apiActionConstants'
import fetchLoyaltyAccount from 'actions/loyalty/fetchLoyaltyAccount'
import type { AppAction } from 'actions/types/ActionTypes'
import requestLoyaltyAccountReopen from 'api/loyalty/requestLoyaltyAccountReopen'
import { getCurrentIntl } from 'i18n/util/currentIntl'
import getLoyaltyAccountClosedErrorCode from 'loyalty/lib/getLoyaltyAccountClosedErrorCode'
import getLoyaltyClosedAccountTier from 'loyalty/lib/getLoyaltyClosedAccountTier'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'

/**
 * Reopens the signed-in customer's Société account after an admin closed it, then refetches it.
 *
 * Only an account closed by an admin (`ACCOUNT_CLOSED`) can be reopened this way; for any other
 * account that failed to load, e.g. when IBS can't read the membership at all, only Customer Service
 * can help, so the customer is pointed to them instead. The welcome back snackbar waits for
 * the refetch, since that is what replaces the closed-account hero with the member page. Retrying
 * after a failed refetch is safe: reopening an open account is a no-op.
 */
function reopenLoyaltyAccount(): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const memberId = state.auth.account.memberId
    const accountError = state.loyalty.account.error

    if (!memberId || !accountError || state.loyalty.reopeningAccount) return

    if (getLoyaltyAccountClosedErrorCode(accountError) !== 'ACCOUNT_CLOSED') {
      const intl = getCurrentIntl()
      showSnackbar(intl.formatMessage({
        id: 'ysdIRl_HsZ49fmQYhZtg',
        defaultMessage: 'To reopen your account, please contact our Customer Service team.',
        description: 'Snackbar shown when a customer tries to reopen a Société account that can only be reopened by Customer Service',
      }), 'warning')
      return
    }

    const isInfiniteTier = getLoyaltyClosedAccountTier(accountError) === 'infinite'

    dispatch({
      type: API_CALL,
      api: REOPEN_LOYALTY_ACCOUNT,
      request: () => requestLoyaltyAccountReopen(memberId)
        .then(() => dispatch(fetchLoyaltyAccount(undefined, { force: true })))
        .then((refreshed) => {
          if (!refreshed) {
            throw new Error('The reopened Société account could not be refreshed')
          }

          const intl = getCurrentIntl()
          const welcomeBackToInfinite = intl.formatMessage({
            id: 'x6AMQgb0b4dJY6duy9ff',
            defaultMessage: 'Welcome back to Infinite',
            description: 'Snackbar message after an Infinite member reopens their closed Infinite account',
          })
          const welcomeBackToSociete = intl.formatMessage({
            id: '2rKS5KI4PPzAe5AAELFz',
            defaultMessage: 'Welcome back to Société',
            description: 'Snackbar message after a customer reopens their closed Société account',
          })
          showSnackbar(isInfiniteTier ? welcomeBackToInfinite : welcomeBackToSociete, 'success')
        })
        .catch((err) => {
          const intl = getCurrentIntl()
          showSnackbar(
            intl.formatMessage({
              id: 'YcSJdZ2rWltHFKoRuqEi',
              defaultMessage: 'Please try again later or contact customer service.',
              description: 'Snackbar body message when reopening a closed Société account fails',
            }),
            'critical',
            {
              heading: intl.formatMessage({
                id: 'WKc2TR-f3ZvG952Ne-h4',
                defaultMessage: 'Oops! Something went wrong',
                description: 'Snackbar heading when reopening a closed Société account fails',
              }),
            },
          )

          throw err
        }),
    })
  }
}

export default reopenLoyaltyAccount
